import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";
import type { User } from "@/lib/types/auth.types";

/**
 * Profile API.
 *
 * Talks to the real Express middleware at `/api/user/*` (the same host the
 * session cookie is already scoped to — see axiosSlice/authSlice), routed
 * through `axiosBaseQuery` so an expired access token is refreshed and a
 * disabled account is signed out the same way every other authenticated call
 * behaves.
 *
 * `getProfile` hits the same `/api/user/profile` endpoint `authSlice` uses for
 * the session-restore check, just typed against the fuller `User` shape so the
 * settings form can read fields (job title, phone, LinkedIn, offerings) that
 * `CurrentUser` doesn't carry.
 */

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  companyName?: string;
  jobTitle?: string;
  mobilePhone?: string;
  ccode?: string;
  linkedinId?: string;
  offerings?: string[];
}

interface Envelope<T> {
  success?: boolean;
  message?: string;
  data: T;
}

export const profileApi = createApi({
  reducerPath: "profileApi",
  baseQuery: axiosBaseQuery("/api/user"),
  tagTypes: ["Profile"],
  refetchOnFocus: true,
  endpoints: (builder) => ({
    getProfile: builder.query<User, void>({
      query: () => ({ url: "/profile", method: "GET" }),
      transformResponse: (response: Envelope<User> | User) =>
        (response as Envelope<User>)?.data ?? (response as User),
      providesTags: ["Profile"],
    }),

    updateProfile: builder.mutation<User, UpdateProfileRequest>({
      query: (body) => ({ url: "/profile/update", method: "POST", body }),
      transformResponse: (response: Envelope<User> | User) =>
        (response as Envelope<User>)?.data ?? (response as User),
      invalidatesTags: ["Profile"],
    }),
  }),
});

export const { useGetProfileQuery, useUpdateProfileMutation } = profileApi;

export default profileApi;
